import { Box, Flex, NumberInput, NumberInputField, Text } from '@elkaso-app/web-design';
import { useCreateDealFromMasterApi, useUpdateExclusiveDealApi } from 'apis/exclusive-deals';
import { getFamilyPriceMatrixEndpointIdentifier } from 'apis/exclusive-deals/use-get-family-price-matrix-api';
import { PriceHistoryPopover } from 'pages/prices/components/price-history-popover';
import type { MatrixCell } from 'pages/prices/utils/build-price-matrix';
import { formatPrice, freshness, pctInfo } from 'pages/prices/utils/matrix-display';
import type { VariantUom } from 'pages/prices/utils/uom-display';
import { dealUnitWord, derivedPriceLines, variantLadder } from 'pages/prices/utils/uom-display';
import type { MouseEvent } from 'react';
import { useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import type { PriceTier } from 'utils/price-units';
import { availableTiers, resolveTier } from 'utils/price-units';

export interface PriceCellProps {
  cell?: MatrixCell;
  supplierId: number;
  masterProductId: number;
  // The variant's UOM ladder. The committed price is per the deal's stored
  // rung (dealUnit; outermost rung for pre-dealUnit deals); when present, the
  // cell restates the figure on the other rungs — live while typing, muted
  // under the committed price — and editing offers the rungs as unit chips.
  variant?: VariantUom;
}

// One (variant, supplier) matrix cell, per the design handoff: right-aligned
// price + freshness + "lowest" label; click switches to an inline input with
// an accent ring and a live %-change pill (warns on suspicious jumps).
// Enter/blur commits, Escape cancels. A cell with no deal renders "—" and is
// just as editable — committing there creates the deal (the BE resolves or
// creates the supplier product from the master product).
export const PriceCell = ({ cell, supplierId, masterProductId, variant }: PriceCellProps) => {
  const queryClient = useQueryClient();
  const { mutate: updateDeal, isLoading: isUpdating } = useUpdateExclusiveDealApi();
  const { mutate: createDeal, isLoading: isCreating } = useCreateDealFromMasterApi();

  const isSaving = isUpdating || isCreating;

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  // The rung the price being typed is agreed against. Seeded from the deal's
  // stored unit (outermost rung for a new/pre-dealUnit cell); null when the
  // variant defines no rung, in which case no unit is offered or sent.
  const [tier, setTier] = useState<PriceTier | null>(null);
  // Escape unmounts the input, which can still fire its blur handler — the
  // flag stops that blur from committing the cancelled value.
  const isCancelledRef = useRef(false);

  const fresh = cell ? freshness(cell.lastModifiedAt) : null;
  const ladder = variant ? variantLadder(variant) : {};
  const tierOptions = variant ? availableTiers(ladder) : [];

  const startEdit = () => {
    if (isSaving) return;
    isCancelledRef.current = false;
    setValue(cell?.price != null ? String(cell.price) : '');
    setTier(resolveTier(ladder, cell?.dealUnit));
    setIsEditing(true);
  };

  const cancel = () => {
    isCancelledRef.current = true;
    setIsEditing(false);
  };

  const refetchMatrix = () => queryClient.invalidateQueries([getFamilyPriceMatrixEndpointIdentifier]);

  const commit = () => {
    setIsEditing(false);

    const price = Number(value);
    if (!value.trim() || !Number.isFinite(price) || price <= 0) return;

    const dealUnit = tier ?? undefined;

    if (!cell) {
      createDeal({ body: { supplierId, masterProductId, dealPrice: price, dealUnit } }, { onSuccess: refetchMatrix });
      return;
    }

    const unitUnchanged = tier == null || tier === resolveTier(ladder, cell.dealUnit);
    if (cell.price != null && price === cell.price && unitUnchanged) return;
    updateDeal({ dealId: cell.dealId, body: { dealPrice: price, dealUnit } }, { onSuccess: refetchMatrix });
  };

  if (isEditing) {
    const pct = cell && value.trim() ? pctInfo(Number(value), cell.price) : null;
    const derived = variant ? derivedPriceLines(variant, Number(value), tier) : [];

    return (
      <Box px='8px' py='5px' boxShadow='inset 0 0 0 2px var(--chakra-colors-red-500)' borderRadius='2px' bg='white'>
        <NumberInput size='sm' min={0} value={value} onChange={(valueAsString) => setValue(valueAsString)}>
          <NumberInputField
            autoFocus
            textAlign='end'
            fontWeight='bold'
            border='none'
            px='4px'
            onFocus={(e) => e.currentTarget.select()}
            onBlur={() => {
              if (isCancelledRef.current) return;
              commit();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') cancel();
            }}
          />
        </NumberInput>
        {/* Which rung the typed figure is agreed against — chips, not a
            dropdown, so retargeting is one click inside a spreadsheet-dense
            cell. mousedown is prevented so picking a unit never blurs the
            input (a blur would commit mid-edit). Hidden when the variant
            defines a single rung — nothing to choose. */}
        {tierOptions.length > 1 && (
          <Flex justify='flex-end' gap='3px' mt='3px' wrap='wrap'>
            {tierOptions.map((option) => (
              <Text
                key={option.tier}
                as='button'
                type='button'
                onMouseDown={(e: MouseEvent) => {
                  e.preventDefault();
                  setTier(option.tier);
                }}
                fontSize='10px'
                fontWeight={tier === option.tier ? 'bold' : 'medium'}
                color={tier === option.tier ? 'white' : 'gray.600'}
                bg={tier === option.tier ? 'red.500' : 'gray.100'}
                px='6px'
                py='1px'
                borderRadius='4px'
                cursor='pointer'>
                / {option.unit}
              </Text>
            ))}
          </Flex>
        )}
        {derived.length > 0 && (
          <Box textAlign='end' mt='2px'>
            {derived.map((line) => (
              <Text
                key={line}
                fontSize='10.5px'
                color='gray.500'
                lineHeight='1.5'
                whiteSpace='nowrap'
                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                ≈ {line}
              </Text>
            ))}
          </Box>
        )}
        {pct && (
          <Box textAlign='end' mt='2px'>
            <Text
              as='span'
              fontSize='10.5px'
              fontWeight='bold'
              color={pct.color}
              bg={pct.bg}
              px='6px'
              borderRadius='5px'>
              {pct.label}
            </Text>
            {pct.warn && (
              <Text fontSize='9.5px' fontWeight='semibold' color='#C0392B'>
                ±25% — check?
              </Text>
            )}
          </Box>
        )}
      </Box>
    );
  }

  if (!cell) {
    return (
      <Box
        onClick={startEdit}
        cursor='text'
        px='10px'
        py='10px'
        minH='44px'
        textAlign='end'
        opacity={isSaving ? 0.6 : 1}
        role='button'>
        <Text fontSize='15px' color='#C9CCD3'>
          —
        </Text>
      </Box>
    );
  }

  const committedDerived = variant ? derivedPriceLines(variant, cell.price, cell.dealUnit) : [];
  const committedUnit = variant ? dealUnitWord(variant, cell.dealUnit) : null;

  return (
    <Box
      onClick={startEdit}
      cursor='text'
      position='relative'
      data-group
      px='10px'
      py='10px'
      minH='44px'
      textAlign='end'
      opacity={cell.isActive ? (isSaving ? 0.6 : 1) : 0.4}
      role='button'>
      {/* Revealed on cell hover so the dense grid stays quiet (always visible
          below md — touch has no hover); stops click propagation so opening
          the history never flips the cell to edit. Bottom-left corner — cell
          content is right-aligned, so that corner stays clear of the price
          digits the top corner overlapped. */}
      <Box
        position='absolute'
        insetStart='3px'
        bottom='3px'
        opacity={{ base: 1, md: 0 }}
        transition='opacity 0.12s'
        _groupHover={{ opacity: 1 }}
        _focusWithin={{ opacity: 1 }}
        onClick={(e) => e.stopPropagation()}>
        <PriceHistoryPopover dealId={cell.dealId} variant={variant} />
      </Box>
      <Text
        fontSize='14.5px'
        fontWeight={cell.isLowest ? 'bold' : 'semibold'}
        color={cell.isLowest ? '#157F4A' : 'gray.800'}
        sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatPrice(cell.price)}
        <Text as='span' fontSize='9.5px' fontWeight='semibold' color='gray.400' ms='3px'>
          {/* The stored rung's word — the price's actual meaning, not the row
              convention (suppliers can quote different rungs on one row). */}
          {committedUnit ? `AED/${committedUnit}` : 'AED'}
        </Text>
      </Text>
      {committedDerived.map((line) => (
        <Text
          key={line}
          fontSize='10.5px'
          color='gray.500'
          lineHeight='1.5'
          whiteSpace='nowrap'
          sx={{ fontVariantNumeric: 'tabular-nums' }}>
          ≈ {line}
        </Text>
      ))}
      {fresh && (
        <Text fontSize='10.5px' fontWeight='medium' color={fresh.color}>
          {fresh.label}
        </Text>
      )}
      {cell.isLowest && (
        <Text fontSize='9.5px' fontWeight='bold' letterSpacing='0.06em' textTransform='uppercase' color='#157F4A'>
          lowest
        </Text>
      )}
    </Box>
  );
};
